import {Component, OnInit} from '@angular/core';
import {LearnerStatistic, ManagerLearnerStatisticInterface} from '../models/learner-statistic.model';
import {
  ManagerStatisticSummaryInterface,
  ManagerTopStatistics
} from '../models/avg-learners-statistic.model';
import {TableConfig, CardConfig, SelectorConfig} from '../models/ui-config.models';
import {
  ALL_SCENARIOS,
  ALL_SCENARIO_ID,
  LEARNER,
  MANAGER_CARDS,
  MANAGER_TABLE_COLUMNS,
  MY_TEAM,
  SELECTOR_CONFIGS
} from '../constants/calls-team-dashboard.constant';
import {AiScenario, UserAiStatistics} from '../models/top-level-statistic.model';
import {Manager} from '../models/manager.model';
import {AllScorecardInfo, ScorecardSection, TopStatisticsInfo} from '../models/ai-scorecard-statistic.model';
import {ScorecardSectionProcessor, SectionDisplayData} from '../models/scorecard-processor.model';
import {TABS_NANAGER } from '../constants/data-model.const';
import { ManagerDashboardService } from '../services/manager-dashboard.service';
import { ExportCallsPayload } from '../services/my-development.service';

@Component({
  selector: 'app-calls-team-dashboard-page',
  templateUrl: './calls-team-dashboard.page.html',
  styleUrls: ['./calls-team-dashboard.page.scss'],
})

export class CallsTeamDashboardPage implements OnInit {
  public selectedTab = 'learners';
  public tabs = TABS_NANAGER;

  protected allStats: AllScorecardInfo;
  protected sectionStats: SectionDisplayData[];

  protected tableColumns: TableConfig[] = MANAGER_TABLE_COLUMNS;
  protected cards: CardConfig[] = MANAGER_CARDS;
  protected selectors: SelectorConfig[] = SELECTOR_CONFIGS;

  protected managers: Manager[];
  protected allScenarios: AiScenario[];
  protected isAllScenarios = true;
  protected selectedScenarios: AiScenario[];
  protected users: UserAiStatistics[];
  protected learnersStatistic: ManagerLearnerStatisticInterface[];
  protected isAllLearners = true;
  protected isIncludeTeams = false;
  protected includeTeams = false;
  protected selectedLearnerIds: string[];
  protected topStatistics: ManagerStatisticSummaryInterface;

  private rawCallsList = null;
  protected filteredCallsList = null;
  protected callDetails = null;
  protected showLearnerDetails = false;

  protected selectedManagerUserId: string | null = null;
  protected selectedAiAgentIds: number[] = [];

  constructor(
    private managerDashboardService: ManagerDashboardService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    const managers = await this.managerDashboardService.getManagers();
    this.managers = managers;
    this.isIncludeTeams = managers.length > 0;

    const managerConfig = this.getSelectorConfig('manager');
    managerConfig.options = [MY_TEAM, ...managers.map(m => ({
      title: m.full_name,
      value: m.userId
    }))];

    this.allScenarios = await this.managerDashboardService.getAllScenarios();
    const scenarioConfig = this.getSelectorConfig('scenarios');
    scenarioConfig.options = [ALL_SCENARIOS, ...this.allScenarios.map(s => ({
      title: s.title,
      value: s.id
    }))];

    this.users = await this.managerDashboardService.getMyTeam();

    this.selectedScenarios = [...this.allScenarios];
    this.selectedAiAgentIds = this.selectedScenarios.map(s => s.id);
    this.selectedLearnerIds = this.users.map(s => s.id);

    const learnerConfig = this.getSelectorConfig('learner');
    learnerConfig.options = [LEARNER, ...this.users.map(s => ({
      title: `${s.first_name} ${s.last_name}`,
      value: s.id
    }))];

    this.calculateStatistics();
  }

  public selectTab(tab: string) {
    if (tab === 'learners') {
      this.filteredCallsList = null;
      this.showLearnerDetails = false;
    }
    this.selectedTab = tab;
    this.calculateStatistics();
  }

  public onSelectorsChange(change: { key: string; values: Record<string, any> }) {
    const {key, values} = change;
    switch (key) {
      case 'scenarios':
        this.filterByScenarios(values[key] as number[]);
        break;

      case 'manager':
        this.filterByManager(values[key] as string);
        this.selectTab('learners');
        break;

      case 'learner':
        this.filterByLearners(values[key] as string[]);
        this.selectTab('learners');
        break;

      case 'includeTeams':
        this.filterByIncludeTeams(values[key] as boolean);
        break;

      default:
        break;
    }
  }

  protected calculateStatistics() {
    this.learnersStatistic = this.mapLearnerStatistics(this.users, this.selectedScenarios);
    if (this.selectedTab === 'scorecard' || this.selectedTab === 'heatmap') {
      this.getScorecardStats().then((stat) => {
        this.topStatistics = new ManagerTopStatistics(stat);
      });
    } else if (this.selectedTab === 'learners' || this.selectedTab === 'callslog') {
      this.getDashboardTopStats().then((stat) => {
        this.topStatistics = new ManagerTopStatistics(stat);
      });
    }
  }

  private filterByScenarios(values: number[]) {
    if (values.includes(ALL_SCENARIO_ID)) {
      this.isAllScenarios = true;
      this.selectedScenarios = [...this.allScenarios];
      this.selectedAiAgentIds = this.selectedScenarios.map(s => s.id);
      this.filteredCallsList = this.rawCallsList ? [...this.rawCallsList] : null;
    } else {
      this.isAllScenarios = false;
      this.selectedScenarios = this.allScenarios.filter(s => values.includes(s.id));
      this.selectedAiAgentIds = this.selectedScenarios.map(s => s.id);
      const agentsFirstNames = this.selectedScenarios.map(s => s.AiScenarioAgents[0].agentFirstName);
      this.filteredCallsList = this.rawCallsList?.filter(c => agentsFirstNames.includes(c.agentFirstName)) || null;
    }

    this.calculateStatistics();
  }

  private async filterByManager(managerUserId: string): Promise<void> {
    try {
      this.users = await this.managerDashboardService.getTeamByManagerId(managerUserId);
    } catch (err) {
      console.error('Error: ', err);
      this.users = [];
    }

    const selectedManager = this.managers.find(m => m.userId === managerUserId);
    const selectedManagerId = selectedManager ? selectedManager.id : null;

    this.selectedManagerUserId = selectedManager ? selectedManager.userId : null;

    const baseManagers = this.managers.filter(
      m => m.userId === managerUserId
    );

    let fetchedManagers: Manager[];
    try {
      fetchedManagers = await this.managerDashboardService.getManagers(selectedManagerId);
    } catch (err) {
      console.error('Error: ', err);
      fetchedManagers = [];
    }

    this.isIncludeTeams = fetchedManagers.length > 0;

    this.managers = [
      ...baseManagers,
      ...fetchedManagers
    ];

    console.log('this.managers: ', this.managers);

    this.updateFilterParams(managerUserId);
    this.calculateStatistics();
  }

  private updateFilterParams(managerId: string): void {
    this.selectors = this.selectors.map(selector => {
      switch (selector.key) {
        case 'learner':
          return {
          ...selector,
          options: [
            LEARNER,
            ...this.users.map(s => ({
              title: `${s.first_name} ${s.last_name}`,
              value: s.id,
            })),
          ],
          value: [''],
          prevValue: [''],
        };
        case 'scenarios':
          return {
          ...selector,
          value: [ALL_SCENARIO_ID],
          prevValue: [ALL_SCENARIO_ID],
        };
        case 'manager':
          return {
          ...selector,
          options: [
            MY_TEAM,
            ...this.managers.map(m => ({
              title: (managerId !== '' && m.userId !== managerId ? '- ' : '') + m.full_name,
              value: m.userId,
            })),
          ],
          value: managerId,
        };
        default:
          return selector;
      }
    });

    this.selectedScenarios = [...this.allScenarios];
    this.selectedLearnerIds = this.users.map(s => s.id);
  }

  private filterByLearners(values: string[]) {
    if (values.includes('')) {
      this.isAllLearners = true;
      this.selectedLearnerIds = this.users.map(s => s.id);
    } else {
      this.isAllLearners = false;
      this.selectedLearnerIds = [...values];
    }

    this.calculateStatistics();
  }

  private filterByIncludeTeams(value: boolean) {
    if (value) {
      this.includeTeams = true;
    } else {
      this.includeTeams = false;
    }

    this.calculateStatistics();
  }

  private updateSelectorValue(key: string, value: any): void {
    const configIndex = this.selectors.findIndex(c => c.key === key);
    if (configIndex === -1) {
      return;
    }

    const config = this.selectors[configIndex];

    const updatedConfig = {
      ...config,
      value: Array.isArray(value) ? [...value] : value,
      prevValue: Array.isArray(value) ? [...value] : value
    };

    this.selectors = [
      ...this.selectors.slice(0, configIndex),
      updatedConfig,
      ...this.selectors.slice(configIndex + 1)
    ];
  }

  protected async getScorecardStats(): Promise<TopStatisticsInfo> {
    const params = {
      users: this.mapSelectedLearners(),
      aiAgents: this.mapSelectedAgents(),
      includeIndirects: this.includeTeams && this.isIncludeTeams,
    };

    const data = await this.managerDashboardService.getDashboardScorecardStats(params);

    this.allStats = data;
    this.sectionStats = this.allStats.sections.map(
      (d: ScorecardSection) => new ScorecardSectionProcessor(d)
    );
    return data;
  }

  protected getDashboardTopStats(): Promise<TopStatisticsInfo> {
    const params = {
      users: this.mapSelectedLearners(),
      aiAgents: this.mapSelectedAgents(),
      includeIndirects: this.includeTeams && this.isIncludeTeams,
    };

    return this.managerDashboardService.getDashboardTopStats(params);
  }

  private getSelectorConfig(key: string): SelectorConfig {
    return this.selectors.find(c => c.key === key);
  }

  private mapLearnerStatistics(data: UserAiStatistics[], scenarios: AiScenario[]): ManagerLearnerStatisticInterface[] {
    return data.filter(d => this.selectedLearnerIds
      .includes(d.id))
      .map(d => new LearnerStatistic(
        d.id,
        d.sub_id,
        d.first_name,
        d.last_name,
        scenarios,
        d.AiScorecardDetailsByUser
      ));
  }

  private mapSelectedAgents(): number[] {
    return this.selectedScenarios.reduce((acc, scenario) => {
      const agents = scenario.AiScenarioAgents.map(a => a.id);
      return [...acc, ...agents];
    }, []);
  }

  private mapSelectedLearners(): string[] {
    return this.users
      .map(s => s.id)
      .filter(id => this.selectedLearnerIds.includes(id));
  }

  protected async openLearnerStatistic(learner: ManagerLearnerStatisticInterface) {
    this.updateSelectorValue('learner', [learner.userId]);
    this.filterByLearners([learner.userId]);

    const calls = await this.managerDashboardService.getCallsForLearner(learner.delegateId.toString());
    this.rawCallsList = calls;

    const agentsFirstNames = this.selectedScenarios.map(s => s.AiScenarioAgents[0].agentFirstName);
    this.filteredCallsList = calls.filter(d => agentsFirstNames.includes(d.agentFirstName));

    this.selectTab('callslog');
  }


  public async viewRecord(id: string): Promise<void> {
    this.callDetails = await this.managerDashboardService.getCallDetails(id);
    this.showLearnerDetails = true;
  }

  public handleBack() {
    this.showLearnerDetails = false;
  }

  public async getPayload(includeTeams: boolean = false): Promise<ExportCallsPayload> {
    const payload: ExportCallsPayload = {
      managerId: this.selectedManagerUserId,
      delegateIds: this.isAllLearners ? [] : [...this.selectedLearnerIds],
      // delegateIds: [...this.selectedLearnerIds],
      aiAgentIds: this.isAllScenarios ? [] : this.selectedAiAgentIds,
      includeIndirects: includeTeams,
    };

    return payload;
  }

}
